import { Loader2 } from 'lucide-react';
import { cn } from '@hqo/react-hooks';

/** Row height, and therefore how far the content travels to fully reveal it. Matches `h-12` / `-mt-12`. */
export const INDICATOR_HEIGHT = 48;
/** Degrees of icon rotation per px of pull, so the drag itself reads as progress before release. */
const ROTATION_PER_PX = 3;

interface PullIndicatorProps {
  /** Live post-resistance drag distance; 0 when idle. Drives opacity and icon rotation only. */
  distance: number;
  /** Held open (action running, or a message showing) — pin opacity and spin the icon. */
  active: boolean;
  /** Replaces the spinner entirely with a terminal message. */
  message?: string;
  dataTestId?: string;
}

/**
 * The pull-to-refresh affordance.
 *
 * It does NOT size itself to the drag. The row is a fixed height tucked out of view behind a negative
 * margin, and the PARENT reveals it by translating the whole content block. That keeps the animation on
 * the compositor: a height-per-frame approach reflows the entire card list on every touchmove, which is
 * exactly what made the first version stutter under the thumb.
 */
export const PullIndicator = ({
  distance,
  active,
  message,
  dataTestId,
}: PullIndicatorProps): JSX.Element => {
  const revealed = active ? INDICATOR_HEIGHT : distance;
  const opacity = Math.min(revealed / INDICATOR_HEIGHT, 1);
  // Winds toward the pull, so the drag reads as progress before release.
  const rotation = distance * ROTATION_PER_PX;

  return (
    <div
      className="-mt-12 flex h-12 items-center justify-center"
      data-testid={dataTestId}
      // Entirely decorative: the feed keeps ONE persistent aria-live region that announces these messages,
      // because iOS VoiceOver is unreliable about reading a live node inserted with its text already set.
      // Announcing from here too would double up wherever it does work.
      aria-hidden
    >
      {message ? (
        <span className="text-sm text-system-grey-dark" style={{ opacity }}>
          {message}
        </span>
      ) : (
        <Loader2
          className={cn('size-6 text-system-grey-medium', active && 'animate-spin')}
          style={{ opacity, transform: active ? undefined : `rotate(${rotation}deg)` }}
          aria-hidden
        />
      )}
    </div>
  );
};
